/**
 * Error Boundary Component
 * Phase 5.5.1: React Error Boundaries
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI.
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, Home, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { errorReporter } from '../services/errorReporter';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Whether to show the "Go Home" button */
  showHomeButton?: boolean;
  /** Whether to show the "Report Issue" button */
  showReportButton?: boolean;
  /** Custom home URL */
  homeUrl?: string;
  /** Custom report URL */
  reportUrl?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to error reporter
    errorReporter.reportComponentError(error, errorInfo.componentStack ?? undefined, {
      boundary: 'ErrorBoundary',
    });

    // Update state with error info
    this.setState({ errorInfo });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = this.props.homeUrl || '/';
  };

  handleReport = (): void => {
    const reportUrl = this.props.reportUrl || 'https://github.com/your-org/your-repo/issues/new';
    const errorDetails = this.state.error
      ? encodeURIComponent(`Error: ${this.state.error.message}\n\nURL: ${window.location.href}`)
      : '';
    window.open(`${reportUrl}?body=${errorDetails}`, '_blank');
  };

  render(): ReactNode {
    const { showHomeButton = true, showReportButton = true } = this.props;

    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="flex min-h-[400px] items-center justify-center p-6" role="alert">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-6 w-6 text-destructive" aria-hidden="true" />
              </div>
              <CardTitle>Something went wrong</CardTitle>
              <CardDescription>We're sorry, but something unexpected happened.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {import.meta.env.DEV && this.state.error && (
                <div className="rounded-md bg-muted p-3">
                  <p className="text-xs font-mono text-muted-foreground break-all">
                    {this.state.error.message}
                  </p>
                  {this.state.error.stack && (
                    <details className="mt-2">
                      <summary className="text-xs text-muted-foreground cursor-pointer">
                        Stack trace
                      </summary>
                      <pre className="mt-2 text-xs font-mono text-muted-foreground whitespace-pre-wrap overflow-auto max-h-40">
                        {this.state.error.stack}
                      </pre>
                    </details>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  className="flex-1 min-w-[120px]"
                  onClick={this.handleReset}
                  aria-label="Try again"
                >
                  <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                  Try Again
                </Button>
                {showHomeButton && (
                  <Button
                    variant="outline"
                    className="flex-1 min-w-[120px]"
                    onClick={this.handleGoHome}
                    aria-label="Go to home page"
                  >
                    <Home className="mr-2 h-4 w-4" aria-hidden="true" />
                    Go Home
                  </Button>
                )}
              </div>
              {showReportButton && (
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={this.handleReport}
                  aria-label="Report this issue"
                >
                  <MessageSquare className="mr-2 h-4 w-4" aria-hidden="true" />
                  Report Issue
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook-based error boundary wrapper for functional components
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  fallback?: ReactNode
): React.FC<P> {
  return function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary fallback={fallback}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}
